import { inject, Injectable } from '@angular/core';
import { TranslocoHttpLoader } from '@app/core/transloco/transloco-http.loader';
import { DefaultTranspiler, flatten, type HashMap, type Translation, type TranslocoMissingHandler, type TranslocoTranspiler } from '@ngneat/transloco';
import type { SafeAny } from '@shared/types/any.type';
import { finalize, firstValueFrom, Subject, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslocoTranspilerLoader extends DefaultTranspiler implements TranslocoTranspiler {
  protected readonly langMap: Map<string, Translation> = new Map<string, Translation>();

  public override transpile(value: any, params: HashMap | undefined, translation: Translation, key: string): any {
    return super.transpile(value, params, this.setTranslation(translation), key);
  }

  public getTranslation(lang: string = localStorage.getItem('webLanguage') ?? 'en'): Translation {
    return this.langMap.get(lang) ?? {};
  }

  public getLang(): string {
    return localStorage.getItem('webLanguage') ?? 'en';
  }

  public setTranslation(translation: Translation, lang: string = localStorage.getItem('webLanguage') ?? 'en'): Translation {
    const updatedTranslation = {
      ...this.langMap.get(lang),
      ...translation
    };
    this.langMap.set(lang, updatedTranslation);

    return updatedTranslation;
  }
}

@Injectable({ providedIn: 'root' })
export class TranslocoMissingHandlerController {
  public readonly event: Subject<{ translation: Translation; lang: string }> = new Subject<{ translation: Translation; lang: string }>();
  public readonly availableScopes: string[] = [
    'analytics',
    'billing',
    'chartering',
    'database',
    'elxa_controls',
    'port_expense',
    'port_operation',
    'ship_interface',
    'ship_shore_operation'
  ];
  public readonly scopeMapping: Record<string, string[]> = {
    analytics: ['analytics'],
    billing: ['billing'],
    chartering: ['chartering'],
    database: ['database'],
    'elxa-controls': ['elxa_controls'],
    'port-expense': ['port_expense'],
    'port-operation': ['port_operation'],
    'ship-interface': ['ship_interface'],
    'ship-shore-operation': ['ship_shore_operation']
  };
  protected readonly translates: Map<string, Translation> = new Map<string, Translation>();
  protected readonly translocoLoader: TranslocoHttpLoader = inject(TranslocoHttpLoader);
  protected readonly queues: Map<string, Date> = new Map<string, Date>();

  public setTranslation(translation: Translation, lang: string): void {
    this.event.next({ translation, lang });
  }

  public load(lang: string, scope: string, key: string = ''): string {
    if (this.queues.has(`${lang}-${scope}`) || this.translates.has(`${lang}-${scope}`)) {
      return key;
    }

    this.queues.set(`${lang}-${scope}`, new Date());

    this.translocoLoader
      .getTranslation(lang, { scope })
      .pipe(
        take(1),
        tap((translation) => {
          const updatedTranslation = flatten({ [scope]: translation });
          this.setTranslation(updatedTranslation, lang);
          this.translates.set(`${lang}-${scope}`, updatedTranslation);
        }),
        finalize(() => this.queues.delete(`${lang}-${scope}`))
      )
      .subscribe();

    return key;
  }

  public async loadByLang(lang: string): Promise<Translation[]> {
    const scopes = this.availableScopes.filter((scope) => !this.translates.has(`${lang}-${scope}`));
    scopes.forEach((scope) => this.queues.set(`${lang}-${scope}`, new Date()));
    return Promise.all(scopes.map((scope) => firstValueFrom(this.translocoLoader.getTranslation(lang, { scope }))))
      .then((translations) => {
        let updatedTranslation = {};
        scopes.forEach((scope, index) => {
          const scopeTranslation = flatten({ [scope]: translations[index] });
          updatedTranslation = { ...updatedTranslation, ...scopeTranslation };
          this.translates.set(`${lang}-${scope}`, scopeTranslation);
        });
        this.setTranslation(updatedTranslation, lang);
        return translations;
      })
      .finally(() => {
        scopes.forEach((scope) => this.queues.delete(`${lang}-${scope}`));
      });
  }

  public async loadByModule(lang: string, module: string): Promise<Translation[]> {
    const foundedScopes = this.scopeMapping[module] ?? ['database'];

    const scopes = foundedScopes.filter((scope) => !this.translates.has(`${lang}-${scope}`));
    scopes.forEach((scope) => this.queues.set(`${lang}-${scope}`, new Date()));

    return Promise.all(scopes.map((scope) => firstValueFrom(this.translocoLoader.getTranslation(lang, { scope }))))
      .then((translations) => {
        let updatedTranslation = {};
        scopes.forEach((scope, index) => {
          const scopeTranslation = flatten({ [scope]: translations[index] });
          updatedTranslation = { ...updatedTranslation, ...scopeTranslation };
          this.translates.set(`${lang}-${scope}`, scopeTranslation);
        });
        this.setTranslation(updatedTranslation, lang);
        return translations;
      })
      .finally(() => {
        scopes.forEach((scope) => this.queues.delete(`${lang}-${scope}`));
        this.loadByLang(lang);
      });
  }
}

@Injectable()
export class TranslocoMissingHandlerLoader implements TranslocoMissingHandler {
  protected readonly translocoTranspiler: TranslocoTranspilerLoader = inject(TranslocoTranspilerLoader);
  protected readonly translocoController: TranslocoMissingHandlerController = inject(TranslocoMissingHandlerController);

  public handle(key: string): SafeAny {
    const scope = typeof key === 'string' ? key.split('.')[0] : '';

    if (!scope || !this.translocoController.availableScopes.includes(scope)) {
      return key;
    }

    const lang = this.translocoTranspiler.getLang();
    const translation = this.translocoTranspiler.getTranslation(lang);

    if (translation[key]) {
      return translation[key];
    }

    return this.translocoController.load(lang, scope, key);
  }
}
